import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUserCheck, FaUsers, FaBuilding, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaEye, FaTimes, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Parse from 'parse';
import AgencyApplication from '../../models/AgencyApplication';
import { convertDiamondsToUsdFormat, getCountryNameByCode } from '../../utils/helpers';
import Agent from '../../models/Agent';

// Styled Components
const Container = styled.div`
  width: 100%;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  .icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px;
    
    &.purple {
      background-color: rgba(99, 102, 241, 0.1);
      color: #6366f1;
    }
    
    &.green {
      background-color: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }
    
    &.blue {
      background-color: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }
    
    &.orange {
      background-color: rgba(245, 158, 11, 0.1);
      color: #f59e0b;
    }
    
    &.red {
      background-color: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
    
    svg {
      font-size: 24px;
    }
  }
  
  .title {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
  
  .value {
    font-size: 1.8rem;
    font-weight: bold;
    color: #333;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: #333;
`;

const AgenciesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #eaeaea;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #333;
  }
  
  tr:hover {
    background-color: #f8f9fa;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  .action-cell {
    width: 100px;
    text-align: center;
  }
  
  .action-button {
    background: none;
    border: none;
    cursor: pointer;
    color: #6366f1;
    padding: 5px;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(99, 102, 241, 0.1);
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoLabel = styled.div`
  width: 200px;
  font-weight: 500;
  color: #666;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 5px;
  }
`;

const InfoValue = styled.div`
  flex: 1;
  color: #333;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const SearchBar = styled.div`
  margin-bottom: 20px;
  
  input {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #eaeaea;
    border-radius: 5px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 5px;
`;

const PageButton = styled.button`
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eaeaea;
  background: ${props => props.$active ? '#6366f1' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? '#6366f1' : '#f5f5f5'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Admin = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total: 0,
    activated: 0,
    suspended: 0
  });
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const itemsPerPage = 10;
  
  // Fetch statistics only once on component mount
  useEffect(() => {
    const fetchStatsAndAgents = async () => {
      setLoading(true);
      try {
        // Get agency application stats
        const query = new Parse.Query(AgencyApplication);
        const total = await query.count();
        
        const activatedQuery = new Parse.Query(AgencyApplication);
        activatedQuery.equalTo(AgencyApplication.keys.STATUS, AgencyApplication.status.ACTIVATED);
        const activated = await activatedQuery.count();
        
        const suspendedQuery = new Parse.Query(AgencyApplication);
        suspendedQuery.equalTo(AgencyApplication.keys.STATUS, AgencyApplication.status.SUSPENDED);
        const suspended = await suspendedQuery.count();
        
        setStats({
          total,
          activated,
          suspended
        });
        
        // Fetch all agents at once
        const agentQuery = new Parse.Query(Agent.className);
        agentQuery.equalTo(Agent.keys.STATUS, Agent.status.ACTIVATED);
        agentQuery.descending(Agent.keys.CREATED_AT);
        agentQuery.include(Agent.keys.AUTHOR);
        agentQuery.limit(1000); // Increase this if needed, but consider performance
        
        const agents = await agentQuery.find();
        console.log('Total agents loaded:', agents.length);
        
        // Store all agents
        setAllAgents(agents);
        
        // Initialize the filtered list with all agents
        setFilteredAgents(agents);
        setTotalPages(Math.ceil(agents.length / itemsPerPage));
      } catch (error) {
        console.error('Error fetching stats and agents:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatsAndAgents();
  }, []); // Empty dependency array means this runs once on mount
  
  // Filter agents when search query changes
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set a new timeout to delay the filtering
    const timeout = setTimeout(() => {
      filterAgents();
    }, 300); // 300ms delay to avoid filtering on every keystroke
    
    setSearchTimeout(timeout);
    
    // Cleanup function
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchQuery]);
  
  // Update the displayed agents when page changes
  useEffect(() => {
    // No need to refetch, just update what's displayed from our filtered list
    const pageAgencies = getCurrentPageAgencies();
    setAgencies(pageAgencies);
  }, [currentPage, filteredAgents]);
  
  // This additional useEffect ensures agencies are populated initially even without any search
  useEffect(() => {
    if (!loading && allAgents.length > 0 && agencies.length === 0) {
      setAgencies(getCurrentPageAgencies());
    }
  }, [loading, allAgents, agencies.length]);
  
  // Filter agents based on search query
  const filterAgents = () => {
    if (!searchQuery.trim()) {
      // If search query is empty, use all agents
      setFilteredAgents(allAgents);
      setTotalPages(Math.ceil(allAgents.length / itemsPerPage));
    } else {
      // Filter agents based on search query
      const lowercaseQuery = searchQuery.toLowerCase();
      const filtered = allAgents.filter(agent => {
        const companyName = agent.get(Agent.keys.COMPANY_NAME) || '';
        const contactEmail = agent.get(Agent.keys.EMAIL) || '';
        const name = agent.get(Agent.keys.NAME) || '';
        
        return (
          companyName.toLowerCase().includes(lowercaseQuery) ||
          contactEmail.toLowerCase().includes(lowercaseQuery) ||
          name.toLowerCase().includes(lowercaseQuery)
        );
      });
      
      setFilteredAgents(filtered);
      setTotalPages(Math.ceil(filtered.length / itemsPerPage));
      // Reset to first page when search changes
      setCurrentPage(1);
    }
  };
  
  // Get agencies for the current page from filtered list
  const getCurrentPageAgencies = () => {
    // If filteredAgents is empty but allAgents has data, use allAgents instead
    const sourceAgents = (filteredAgents.length === 0 && allAgents.length > 0) ? allAgents : filteredAgents;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageAgents = sourceAgents.slice(startIndex, endIndex);
    
    return pageAgents.map(agent => ({
      agent: agent,
      application: null
    }));
  };
  
  const fetchAgencyDetails = async (agencyData) => {
    setLoadingDetails(true);
    try {
      const agent = agencyData.agent;
      const application = agencyData.application;
      
      if (agent) {
        // Fetch hosts count
        const hostsQuery = new Parse.Query('_User');
        hostsQuery.equalTo('agentId', agent.id);
        hostsQuery.equalTo('role', 'host');
        const hostsCount = await hostsQuery.count();
        
        // Get diamonds total from agency
        const diamondsTotal = agent.get(Agent.keys.DIAMONDS_TOTAL) || 0;
        
        // If we don't have an application yet, try to find it
        let appData = application;
        if (!appData) {
          const applicationQuery = new Parse.Query(AgencyApplication);
          applicationQuery.equalTo(AgencyApplication.keys.AGENT_ID, agent.id);
          appData = await applicationQuery.first();
        }
        
        setAgencyDetails({
          agent,
          application: appData,
          hostsCount,
          diamonds: diamondsTotal
        });
      }
    } catch (error) {
      console.error('Error fetching agency details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleViewAgencyDetails = async (agencyData) => {
    setSelectedAgency(agencyData);
    await fetchAgencyDetails(agencyData);
    setShowDetailsModal(true);
  };
  
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedAgency(null);
    setAgencyDetails(null);
  };

  return (
    <Container>
      <SectionTitle>{t('admin.dashboard.title')}</SectionTitle>
      
      <StatsContainer>
        <StatCard>
          <div className="icon blue">
            <FaBuilding />
          </div>
          <div className="title">{t('admin.dashboard.totalAgencies')}</div>
          <div className="value">{loading ? '--' : stats.total}</div>
        </StatCard>
        
        <StatCard>
          <div className="icon green">
            <FaCheckCircle />
          </div>
          <div className="title">{t('admin.dashboard.activeAgencies')}</div>
          <div className="value">{loading ? '--' : stats.activated}</div>
        </StatCard>
        
        <StatCard>
          <div className="icon red">
            <FaExclamationTriangle />
          </div>
          <div className="title">{t('admin.dashboard.suspendedAgencies')}</div>
          <div className="value">{loading ? '--' : stats.suspended}</div>
        </StatCard>
      </StatsContainer>
      
      <SectionTitle>{t('admin.dashboard.activeAgenciesList')}</SectionTitle>
      
      {loading ? (
        <LoadingState>
          <p>{t('common.loading')}</p>
        </LoadingState>
      ) : (
        <>
          <SearchBar>
            <input 
              type="text" 
              placeholder={t('admin.applications.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          
          {agencies.length > 0 ? (
            <>
              <AgenciesTable>
                <thead>
                  <tr>
                    <th>{t('admin.dashboard.agencyName')}</th>
                    <th>{t('admin.dashboard.agencyEmail')}</th>
                    <th>{t('admin.dashboard.hostsCount')}</th>
                    <th>{t('admin.dashboard.totalEarnings')}</th>
                    <th className="action-cell">{t('admin.dashboard.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agencies.map((agencyData, index) => {
                    const agent = agencyData.agent;
                    return (
                      <tr key={index}>
                        <td>{agent.get(Agent.keys.COMPANY_NAME) || agent.get(Agent.keys.NAME) || '-'}</td>
                        <td>{agent.get(Agent.keys.EMAIL) || '-'}</td>
                        <td>{agent.get(Agent.keys.HOST_IDS)?.length || 0}</td>
                        <td>{convertDiamondsToUsdFormat(agent.get(Agent.keys.DIAMONDS_TOTAL) || 0)}</td>
                        <td className="action-cell">
                          <button 
                            className="action-button"
                            onClick={() => handleViewAgencyDetails(agencyData)}
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </AgenciesTable>
              
              {totalPages > 1 && (
                <Pagination>
                  <PageButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &lt;
                  </PageButton>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <PageButton 
                      key={page}
                      $active={currentPage === page}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </PageButton>
                  ))}
                  
                  <PageButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    &gt;
                  </PageButton>
                </Pagination>
              )}
            </>
          ) : searchQuery ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p>{t('admin.dashboard.noSearchResults')} "{searchQuery}"</p>
              <button 
                className="secondary" 
                onClick={() => setSearchQuery('')}
                style={{ 
                  marginTop: '15px',
                  padding: '8px 16px',
                  background: 'transparent',
                  border: '1px solid #6366f1',
                  borderRadius: '5px',
                  color: '#6366f1',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {t('common.clearSearch')}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p>{t('admin.dashboard.noActiveAgencies')}</p>
            </div>
          )}
        </>
      )}
      
      {/* Agency Details Modal */}
      {showDetailsModal && selectedAgency && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeDetailsModal()}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('admin.dashboard.agencyDetails')}</ModalTitle>
              <CloseButton onClick={closeDetailsModal}><FaTimes /></CloseButton>
            </ModalHeader>
            <ModalBody>
              {loadingDetails ? (
                <LoadingState>
                  <p>{t('common.loading')}</p>
                </LoadingState>
              ) : agencyDetails ? (
                <>
                  <SectionTitle>{t('admin.dashboard.agencyInformation')}</SectionTitle>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.agencyName')}</InfoLabel>
                    <InfoValue>
                      {agencyDetails.agent.get(Agent.keys.COMPANY_NAME) || agencyDetails.agent.get(Agent.keys.NAME) || '-'}
                    </InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.contactEmail')}</InfoLabel>
                    <InfoValue>
                      {agencyDetails.agent.get(Agent.keys.EMAIL) || 
                      (agencyDetails.application ? agencyDetails.application.get(AgencyApplication.keys.CONTACT_EMAIL) : '') || 
                      '-'}
                    </InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.createdAt')}</InfoLabel>
                    <InfoValue>
                      {agencyDetails.agent.get(Agent.keys.CREATED_AT) ? 
                        new Date(agencyDetails.agent.get(Agent.keys.CREATED_AT)).toLocaleDateString() : 
                        '-'}
                    </InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.companyRegistrationNumber')}</InfoLabel>
                    <InfoValue>{agencyDetails.agent.get(Agent.keys.COMPANY_REGISTRATION_NUMBER) || '-'}</InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.companyTaxId')}</InfoLabel>
                    <InfoValue>{agencyDetails.agent.get(Agent.keys.COMPANY_TAX_ID) || '-'}</InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.companyAddress')}</InfoLabel>
                    <InfoValue>{agencyDetails.agent.get(Agent.keys.COMPANY_ADDRESS) || '-'}</InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.companyLocation')}</InfoLabel>
                    <InfoValue>
                      {agencyDetails.agent.get(Agent.keys.COMPANY_COUNTRY) && agencyDetails.agent.get(Agent.keys.COMPANY_CITY) ? 
                        `${agencyDetails.agent.get(Agent.keys.COMPANY_CITY)}, ${getCountryNameByCode(agencyDetails.agent.get(Agent.keys.COMPANY_COUNTRY))}` : 
                        '-'}
                    </InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.companyWebsite')}</InfoLabel>
                    <InfoValue>
                      {agencyDetails.agent.get(Agent.keys.COMPANY_WEBSITE) ? 
                        <a href={agencyDetails.agent.get(Agent.keys.COMPANY_WEBSITE)} target="_blank" rel="noopener noreferrer">
                          {agencyDetails.agent.get(Agent.keys.COMPANY_WEBSITE)}
                        </a> : 
                        '-'}
                    </InfoValue>
                  </InfoRow>
                  
                  <SectionTitle>{t('admin.dashboard.agencyPerformance')}</SectionTitle>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.hostsCount')}</InfoLabel>
                    <InfoValue>{agencyDetails.hostsCount || agencyDetails.agent.get(Agent.keys.HOST_IDS)?.length || 0}</InfoValue>
                  </InfoRow>
                  
                  <InfoRow>
                    <InfoLabel>{t('admin.dashboard.totalEarnings')}</InfoLabel>
                    <InfoValue>{convertDiamondsToUsdFormat(agencyDetails.diamonds || 0)}</InfoValue>
                  </InfoRow>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <p>{t('admin.dashboard.agencyDetailsError')}</p>
                </div>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Admin;